"use client"

import { useState } from "react"

// Import all components
import Badge from "./Badge/Badge"
import Button from "./Button/Button"
import Card from "./Card/Card"
import CustomBarChart from "./Charts/BarChart"
import CustomPieChart from "./Charts/PieChart"
import Checkbox from "./Checkbox/Checkbox"
import Input from "./Input/Input"
import Loader from "./Loader/Loader"
import Modal from "./Modal/Moadal"
import RadioGroup from "./RadioGroup/RadioGroup"
import Select from "./Select/Select"
import StatsCard from "./StatsCard/StatsCard"
import Switch from "./Switch/Switch"
import Table from "./table/table"
import Tabs from "./Tabs/Tabs"
import TextArea from "./TextArea/TextArea"
import Tooltip from "./Tooltip/Tooltip"

// Sample data for demonstrations
const sampleChartData = [
  { name: "Jan", value: 400 },
  { name: "Feb", value: 300 },
  { name: "Mar", value: 600 },
  { name: "Apr", value: 800 },
  { name: "May", value: 500 },
]

const samplePieData = [
  { name: "Ready to Distribute", value: 45 },
  { name: "Pending Refurbishment", value: 25 },
  { name: "Needing Warranty", value: 20 },
  { name: "In Transit", value: 10 },
]

const sampleTableData = [
  { id: 1, name: "John Doe", email: "john@example.com", status: "Active", role: "Admin" },
  { id: 2, name: "Jane Smith", email: "jane@example.com", status: "Inactive", role: "User" },
  { id: 3, name: "Bob Johnson", email: "bob@example.com", status: "Active", role: "Manager" },
]

const tableColumns = [
  { key: "name", label: "Name", alignLeft: true },
  { key: "email", label: "Email", alignLeft: true },
  { key: "status", label: "Status" },
  { key: "role", label: "Role" },
]

const ComponentUsageGuide = () => {
  // State for interactive examples
  const [checkboxValue, setCheckboxValue] = useState(false)
  const [switchValue, setSwitchValue] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [textAreaValue, setTextAreaValue] = useState("")
  const [radioValue, setRadioValue] = useState("option1")
  const [selectValue, setSelectValue] = useState("")
  const [activeTab, setActiveTab] = useState("tab1")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTableRows, setSelectedTableRows] = useState([])

  const radioOptions = [
    { value: "option1", label: "Option 1" },
    { value: "option2", label: "Option 2" },
    { value: "option3", label: "Option 3" },
  ]

  const selectOptions = [
    { value: "apple", label: "Apple" },
    { value: "banana", label: "Banana" },
    { value: "orange", label: "Orange" },
  ]

  const tabsData = [
    { id: "tab1", label: "Tab 1", content: <div>Content for Tab 1</div> },
    { id: "tab2", label: "Tab 2", content: <div>Content for Tab 2</div> },
    { id: "tab3", label: "Tab 3", content: <div>Content for Tab 3</div> },
  ]

  return (
    <div style={{ padding: "20px", Width: "100%", margin: "0 auto" }}>
      <h1>Component Usage Guide</h1>
      <p>This guide demonstrates how to use all the reusable components in your project.</p>

      {/* Badge Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Badge Component</h2>
        <p>Use badges to display status indicators, counts, or labels.</p>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
          <Badge variant="default">Default</Badge>
          <Badge variant="primary">Primary</Badge>
          <Badge variant="success">Success</Badge>
          <Badge variant="warning">Warning</Badge>
          <Badge variant="danger">Danger</Badge>
          <Badge variant="outline">Outline</Badge>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <Badge size="xs">Extra Small</Badge>
          <Badge size="small">Small</Badge>
          <Badge size="medium">Medium</Badge>
          <Badge size="large">Large</Badge>
          <Badge dot variant="success">
            With Dot
          </Badge>
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "10px" }}>
          {`<Badge variant="primary">Primary Badge</Badge>
<Badge variant="success" size="small">Small Success</Badge>
<Badge dot variant="warning">With Dot</Badge>`}
        </pre>
      </section>

      {/* Button Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Button Component</h2>
        <p>Interactive buttons with various styles and states.</p>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
          <Button size="small">Small</Button>
          <Button size="medium">Medium</Button>
          <Button size="large">Large</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px" }}>
          {`<Button variant="primary" onClick={handleClick}>
  Click Me
</Button>
<Button variant="outline" size="small" loading>
  Loading Button
</Button>`}
        </pre>
      </section>

      {/* Card Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Card Component</h2>
        <p>Container component for grouping related content.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
          <Card title="Basic Card" variant="default">
            <p>This is a basic card with some content inside.</p>
          </Card>
          <Card
            title="Card with Actions"
            variant="primary"
            headerButtons={[
              { label: "Edit", variant: "secondary", onClick: () => alert("Edit clicked") },
              { label: "Delete", variant: "danger", onClick: () => alert("Delete clicked") },
            ]}
          >
            <p>This card has header action buttons.</p>
          </Card>
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<Card title="My Card" variant="primary">
  <p>Card content goes here</p>
</Card>

<Card 
  title="Card with Actions"
  headerButtons={[
    { label: "Edit", variant: "secondary", onClick: handleEdit }
  ]}
>
  <p>Content with actions</p>
</Card>`}
        </pre>
      </section>

      {/* Charts */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Chart Components</h2>
        <p>Data visualization components for displaying charts and graphs.</p>

        <h3>Bar Chart</h3>
        <div style={{ marginBottom: "20px" }}>
          <CustomBarChart
            data={sampleChartData}
            title="Monthly Sales"
            subtitle="Sales data for the first 5 months"
            height={300}
          />
        </div>

        <h3>Pie Chart</h3>
        <div style={{ marginBottom: "20px" }}>
          <CustomPieChart data={samplePieData} size="medium" showLegendCards={true} />
        </div>

        <pre style={{ background: "#f5f5f5", padding: "10px" }}>
          {`// Bar Chart
<CustomBarChart
  data={chartData}
  title="Monthly Sales"
  height={300}
  showGrid={true}
/>

// Pie Chart
<CustomPieChart
  data={pieData}
  size="medium"
  showLegendCards={true}
/>`}
        </pre>
      </section>

      {/* Form Components */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Form Components</h2>
        <p>Interactive form elements for user input.</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
          <div>
            <h3>Checkbox</h3>
            <Checkbox label="Accept terms and conditions" checked={checkboxValue} onChange={setCheckboxValue} />
            <p>Current value: {checkboxValue.toString()}</p>
          </div>

          <div>
            <h3>Switch</h3>
            <Switch label="Enable notifications" checked={switchValue} onChange={setSwitchValue} />
            <p>Current value: {switchValue.toString()}</p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "20px",
            marginTop: "20px",
          }}
        >
          <div>
            <h3>Input</h3>
            <Input
              label="Email Address"
              placeholder="Enter your email"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              type="email"
              required
            />
          </div>

          <div>
            <h3>Select</h3>
            <Select
              options={selectOptions}
              value={selectValue}
              onChange={setSelectValue}
              placeholder="Choose a fruit"
            />
            <p>Selected: {selectValue}</p>
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>
          <h3>TextArea</h3>
          <TextArea
            label="Description"
            placeholder="Enter a description..."
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
            rows={4}
            maxLength={200}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <h3>Radio Group</h3>
          <RadioGroup options={radioOptions} value={radioValue} onChange={setRadioValue} name="example-radio" />
          <p>Selected: {radioValue}</p>
        </div>

        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`// Checkbox
<Checkbox
  label="Accept terms"
  checked={checked}
  onChange={setChecked}
/>

// Input
<Input
  label="Email"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  type="email"
  required
/>

// Select
<Select
  options={options}
  value={selected}
  onChange={setSelected}
  placeholder="Choose option"
/>`}
        </pre>
      </section>

      {/* Stats Card */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Stats Card Component</h2>
        <p>Display key metrics and statistics.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
          <StatsCard title="Total Devices" value="1,234" changeType="positive" change="+12%" />
          <StatsCard title="Available Devices" value="856" changeType="positive" change="+5%" />
          <StatsCard title="Assigned Devices" value="378" changeType="negative" change="-3%" />
          <StatsCard title="Warranty Expiring" value="45" changeType="neutral" change="No change" />
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<StatsCard
  title="Total Users"
  value="1,234"
  changeType="positive"
  change="+12%"
  onClick={handleCardClick}
/>`}
        </pre>
      </section>

      {/* Table Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Table Component</h2>
        <p>Display tabular data with sorting and selection capabilities.</p>
        <Table
          columns={tableColumns}
          data={sampleTableData}
          showCheckboxes={true}
          onSelectionChange={setSelectedTableRows}
          selectedIds={selectedTableRows}
          isRowClickable={true}
          onRowClick={(row) => alert(`Clicked on ${row.name}`)}
          height={300}
        />
        <p>Selected rows: {selectedTableRows.length}</p>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<Table
  columns={columns}
  data={data}
  showCheckboxes={true}
  onSelectionChange={setSelected}
  isRowClickable={true}
  onRowClick={handleRowClick}
/>`}
        </pre>
      </section>

      {/* Tabs Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Tabs Component</h2>
        <p>Organize content into tabbed sections.</p>
        <Tabs tabs={tabsData} activeTab={activeTab} onChange={setActiveTab} />
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`const tabs = [
  { id: "tab1", label: "Tab 1", content: <div>Content 1</div> },
  { id: "tab2", label: "Tab 2", content: <div>Content 2</div> }
];

<Tabs
  tabs={tabs}
  activeTab={activeTab}
  onChange={setActiveTab}
/>`}
        </pre>
      </section>

      {/* Loader Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Loader Component</h2>
        <p>Loading indicators for different states.</p>
        <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
          <Loader variant="spinner" size="small" />
          <Loader variant="dots" size="medium" />
          <Loader variant="pulse" size="large" />
          <Loader variant="bars" size="medium" />
          <Loader variant="spinner" text="Loading..." />
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<Loader variant="spinner" size="medium" />
<Loader variant="dots" text="Loading..." />
<Loader variant="pulse" centered />`}
        </pre>
      </section>

      {/* Modal Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Modal Component</h2>
        <p>Overlay dialogs for focused interactions.</p>
        <Button onClick={() => setIsModalOpen(true)}>Open Modal</Button>

        <Modal
          title="Example Modal"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="medium"
          footer={
            <div style={{ display: "flex", gap: "10px" }}>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setIsModalOpen(false)}>
                Save
              </Button>
            </div>
          }
        >
          <p>This is the modal content. You can put any content here.</p>
          <p>The modal can be closed by clicking the X button, pressing Escape, or clicking outside.</p>
        </Modal>

        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<Modal
  title="My Modal"
  isOpen={isOpen}
  onClose={() => setIsOpen(false)}
  size="medium"
  footer={<Button onClick={handleSave}>Save</Button>}
>
  <p>Modal content goes here</p>
</Modal>`}
        </pre>
      </section>

      {/* Tooltip Component */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Tooltip Component</h2>
        <p>Contextual information on hover or focus.</p>
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          <Tooltip text="This is a tooltip on top" position="top">
            <Button variant="outline">Hover me (top)</Button>
          </Tooltip>
          <Tooltip text="This is a tooltip on bottom" position="bottom">
            <Button variant="outline">Hover me (bottom)</Button>
          </Tooltip>
          <Tooltip text="This is a tooltip on left" position="left">
            <Button variant="outline">Hover me (left)</Button>
          </Tooltip>
          <Tooltip text="This is a tooltip on right" position="right">
            <Button variant="outline">Hover me (right)</Button>
          </Tooltip>
        </div>
        <pre style={{ background: "#f5f5f5", padding: "10px", marginTop: "20px" }}>
          {`<Tooltip text="Helpful information" position="top">
  <Button>Hover for info</Button>
</Tooltip>`}
        </pre>
      </section>

      {/* Usage Tips */}
      <section style={{ marginBottom: "40px" }}>
        <h2>General Usage Tips</h2>
        <Card title="Best Practices">
          <ul>
            <li>
              <strong>Import only what you need:</strong> Import specific components to keep bundle size small
            </li>
            <li>
              <strong>Use TypeScript:</strong> All components support TypeScript for better development experience
            </li>
            <li>
              <strong>Consistent styling:</strong> Components use CSS modules for scoped styling
            </li>
            <li>
              <strong>Accessibility:</strong> Components include ARIA attributes and keyboard navigation
            </li>
            <li>
              <strong>Responsive design:</strong> Most components adapt to different screen sizes
            </li>
            <li>
              <strong>State management:</strong> Use controlled components for form elements
            </li>
          </ul>
        </Card>
      </section>

      {/* Component Props Reference */}
      <section style={{ marginBottom: "40px" }}>
        <h2>Common Props Patterns</h2>
        <Card title="Prop Patterns">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
            <div>
              <h4>Size Props</h4>
              <p>
                Most components support: <code>xs | small | medium | large | xl</code>
              </p>
            </div>
            <div>
              <h4>Variant Props</h4>
              <p>
                Common variants: <code>default | primary | secondary | success | warning | danger</code>
              </p>
            </div>
            <div>
              <h4>State Props</h4>
              <p>
                Form components: <code>disabled | loading | error | required</code>
              </p>
            </div>
            <div>
              <h4>Event Props</h4>
              <p>
                Callbacks: <code>onClick | onChange | onFocus | onBlur</code>
              </p>
            </div>
          </div>
        </Card>
      </section>
    </div>
  )
}

export default ComponentUsageGuide
